/**
 * Cliente PÚBLICO (guest-first) de la página de profesor (F3-D). Mismo patrón
 * que `fetchCertificateVerification`: un `fetch` plano contra el REST público
 * `/api/public/teachers/:orgSlug/:slug` (sin el cliente tipado ni sesión). Un
 * 404 = perfil inexistente o no público — sin filtrar nada más.
 */

/** Organización emisora (branding público). Espeja `models::PublicOrg`. */
export interface PublicOrgDTO {
  name: string | null;
  slug: string | null;
  logo: string | null;
  branding: unknown | null;
}

/** Profesor público. Espeja `models::PublicTeacher`. */
export interface PublicTeacherDTO {
  name: string | null;
  title: string | null;
  bio: string | null;
  /** JSON de especialidades (array), o null. */
  specialties: unknown | null;
  photo: string | null;
  publicSlug: string | null;
}

/** Tarjeta de curso público. Espeja `models::PublicCourseCard`. */
export interface PublicCourseCardDTO {
  id: string;
  slug: string | null;
  title: string | null;
  description: string | null;
  category: string | null;
  coverImage: string | null;
}

/** Respuesta de `GET /api/public/teachers/:orgSlug/:slug`. */
export interface PublicTeacherPageDTO {
  org: PublicOrgDTO;
  teacher: PublicTeacherDTO;
  courses: PublicCourseCardDTO[];
}

/** Normaliza `specialties` (puede venir como array JSON o string) a `string[]`. */
export function specialtiesToChips(specialties: unknown): string[] {
  if (Array.isArray(specialties)) {
    return specialties.filter((s): s is string => typeof s === "string" && s.trim().length > 0);
  }
  if (typeof specialties === "string" && specialties.trim().length > 0) {
    return specialties
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * Descarga la página pública de un profesor. Lanza (con `.status`) en no-2xx —
 * el consumidor mapea 404 → estado "no encontrado".
 */
export async function fetchPublicTeacherPage(
  baseUrl: string,
  orgSlug: string,
  slug: string,
  opts: { signal?: AbortSignal } = {},
): Promise<PublicTeacherPageDTO> {
  const url = `${baseUrl.replace(/\/$/, "")}/api/public/teachers/${encodeURIComponent(
    orgSlug,
  )}/${encodeURIComponent(slug)}`;
  const res = await fetch(url, { signal: opts.signal });
  if (!res.ok) {
    const err = new Error(`teacher page fetch failed: ${res.status}`) as Error & {
      status?: number;
    };
    err.status = res.status;
    throw err;
  }
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- trust-boundary parse of our server's typed DTO
  return (await res.json()) as PublicTeacherPageDTO;
}
